import React, { useEffect, useState } from 'react';
import { Card, Input, Button, Typography, Space, message, Tag, Row, Col, List } from 'antd';
import { useCommandSocket } from '../../../../../../Hooks/WebSocket/useCommandSocket';

const { Text } = Typography;

interface DeviceCommandCardProps {
  driverUid: string;
}

const CameraCommand: React.FC<DeviceCommandCardProps> = ({ driverUid }) => {
  const { cameraResponses: response, sendCommand, isConnected } = useCommandSocket({ driverUid });
  const [textValue, setTextValue] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [responseList, setResponseList] = useState<any[]>([]);

  useEffect(() => {
    if (!response) return;

    let isEmpty = false;

    if (typeof response === 'string') {
      const trimmed = (response as string).trim();
      isEmpty = trimmed === '' || trimmed === '[]' || trimmed === '{}';
    } else if (typeof response === 'object') {
      isEmpty = response === null || Object.keys(response).length === 0;
    }

    if (!isEmpty) {
      setResponseList((prev) => [{ id: Date.now(), data: response }, ...prev]);
    }
  }, [response]);

  const handleSend = (command: string, text: string) => {
    if (!isConnected) return message.error('Socket not connected!');
    if (!command) return;

    setLoading(true);
    sendCommand(command, text.trim());
    message.info(`Command sent`);

    setTimeout(() => setLoading(false), 1500);
  };

  const handleManualSend = () => handleSend('camera_config', textValue);
  const handleStart = () => handleSend('connect_camera_socket', '');
  const handleStop = () => handleSend('stop_camera_socket', '');

  return (
    <Card
      title={
        <Space>
          <Text strong style={{ fontSize: 16 }}>
            Camera Command
          </Text>
          <Tag color={isConnected ? 'green' : 'red'}>
            {isConnected ? 'Connected' : 'Disconnected'}
          </Tag>
        </Space>
      }
      style={{
        borderRadius: 14,
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        padding: 8,
      }}
    >
      <Row gutter={12} align="middle">
        <Col span={8}>
          <Input
            placeholder="Enter text"
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            onPressEnter={handleManualSend}
          />
        </Col>

        <Col span={10}>
          <Space>
            <Button
              type="primary"
              onClick={handleManualSend}
              loading={loading}
              disabled={!textValue}
            >
              Send
            </Button>
            <Button type="default" onClick={handleStart} disabled={!isConnected}>
              Connect Camera
            </Button>
            <Button type="default" onClick={handleStop} disabled={!isConnected}>
              Stop Camera
            </Button>
          </Space>
        </Col>
      </Row>

      <div style={{ marginTop: 20 }}>
        {responseList.length > 0 ? (
          <Card
            size="small"
            title={<Text strong>Camera Responses</Text>}
            style={{
              borderRadius: 10,
              maxHeight: 400,
              overflowY: 'auto',
            }}
            bodyStyle={{ padding: '10px 14px' }}
          >
            <List
              dataSource={response}
              renderItem={(item, index) => {
                let parsedText = item.text;
                try {
                  parsedText = JSON.parse(item.text);
                } catch (e) {}

                return (
                  <List.Item
                    key={index}
                    style={{
                      borderRadius: 8,
                      marginBottom: 10,
                      padding: '10px 14px',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        color: '#1677ff',
                        marginBottom: 6,
                      }}
                    >
                      Response #{response.length - index}
                    </div>

                    <pre
                      style={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontSize: 13,
                        margin: 0,
                      }}
                    >
                      {typeof parsedText === 'string'
                        ? parsedText
                        : JSON.stringify(parsedText, null, 2)}
                    </pre>
                  </List.Item>
                );
              }}
            />
          </Card>
        ) : (
          <Text
            type="secondary"
            style={{
              display: 'block',
              textAlign: 'center',
              marginTop: 10,
            }}
          >
            No response yet
          </Text>
        )}
      </div>
    </Card>
  );
};

export default CameraCommand;
